<script setup lang="ts">
import AvatarDisplay from '../../components/AvatarDisplay.vue';
import { storeToRefs } from 'pinia';
import { useQuizterStore } from '../../stores/quizterStore';
import { useQrCode } from '../../composables/useQrCode';
import Card from '../../components/Card.vue';
import NextButton from '../../components/NextButton.vue';
import useLocale from '../../../composables/use-locale';
import { Copy } from '@ilteducation/components';
import { ref, nextTick, watch } from 'vue';

const store = useQuizterStore();
const { joinedPlayers, gameId, isLoading, isLobbyFull } = storeToRefs(store);
const { startGame, removePlayer } = store;
const { joinGameUrl, qrCode } = useQrCode(gameId);
const { t } = useLocale();

const handleStartGame = () => {
  startGame();
};

const handleRemovePlayer = (playerId: string) => {
  removePlayer(playerId);
};

const headingRef = ref<HTMLElement | null>(null);
watch(
  [gameId, qrCode],
  async () => {
    if (gameId && qrCode) {
      await nextTick();
      headingRef.value?.focus();
    }
  },
  { immediate: true },
);
</script>

<template>
  <Card :content-max-width="900">
    <div v-if="isLobbyFull" class="d-flex flex-column align-center ga-4">
      <p class="chalkduster-font text-white text-center text-h4 ma-0">{{ t('lobby.lobby_full') }}</p>
    </div>
    <div v-else-if="gameId && qrCode" class="d-flex flex-column align-center ga-4">
      <h1 ref="headingRef" class="text-h2 join-text mb-4" tabindex="-1">{{ t('lobby.join_heading') }}</h1>
      <v-img :src="qrCode" width="200" height="200" class="rounded-lg pa-2" :alt="t('a11y.alt_qr_code')"></v-img>
      <Copy
        v-if="joinGameUrl"
        attach="main"
        :text-to-copy="joinGameUrl"
        :button-text="t('lobby.label_copy_url')"
        data-testid="quizter-join-game-url"
        :data-join-url="joinGameUrl"
        iconCopied="fawsb fa-check"
        iconCopy="fawsb fa-copy"
      />
      <h2 class="text-h5 join-text ma-0 chalkboard-font">{{ t('lobby.join_with_code') }}</h2>
      <h3 class="text-white text-center text-h4 ma-0 mt-2 chalkboard-font">{{ gameId }}</h3>
    </div>
    <div v-else class="d-flex justify-center">
      <v-progress-circular indeterminate color="white" size="64"> </v-progress-circular>
    </div>
    <div v-if="joinedPlayers.length" class="d-flex flex-column ga-4">
      <div
        class="scroll-container ga-3 pa-2 pt-4"
        style="
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
          justify-items: center;
          max-height: 450px;
          overflow-y: auto;
        "
      >
        <AvatarDisplay
          v-for="player in joinedPlayers"
          :key="player.id"
          :player="player"
          :avatar="player.avatar"
          :isActive="!player.offline"
          size="small"
          :show-remove-button="true"
          :isRemoving="store.isRemoving[player.id]"
          @remove="handleRemovePlayer"
        />
      </div>
      <p class="chalkduster-font text-white text-center text-h6 ma-0">
        {{ t('lobby.players_joined', joinedPlayers.length) }}
      </p>
    </div>

    <div v-if="joinedPlayers.length > 0" class="d-flex justify-center mt-8">
      <NextButton size="large" @click="handleStartGame" :loading="isLoading" data-testid="quizter-start-game-button">
        <template #prepend>
          <v-icon class="mr-2" icon="fa:fawsb fa-flag"></v-icon>
        </template>
        {{ t('begin_playing') }}
      </NextButton>
    </div>
  </Card>
</template>

<style>
.scroll-container::-webkit-scrollbar {
  width: 10px;
}

.scroll-container::-webkit-scrollbar-track {
  background: transparent;
}

.scroll-container::-webkit-scrollbar-thumb {
  background-color: rgba(var(--v-theme-primary));
  border-radius: 8px;
  border: 2px solid transparent;
  background-clip: content-box;
}

[tabindex='-1']:focus {
  outline: none;
}
</style>
